import * as React from 'react';
import * as ReactHotToast from 'react-hot-toast';
import {
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Stack,
  useTheme,
} from '@fluentui/react';
import { WARNING_CLASS } from 'utility/toast';

const barType = (t: ReactHotToast.Toast): MessageBarType => {
  if (t.className?.includes(WARNING_CLASS)) return MessageBarType.warning;

  switch (t.type) {
    case 'success':
      return MessageBarType.success;
    case 'error':
      return MessageBarType.error;
    default:
      return MessageBarType.info;
  }
};

export const AppToast: React.FC<{ toast: ReactHotToast.Toast }> = ({
  toast,
}) => {
  const theme = useTheme();
  const type = barType(toast);
  const isInfo = type === MessageBarType.info;
  const isLoading = toast.type === 'loading';
  const message =
    typeof toast.message === 'function' ? toast.message(toast) : toast.message;

  return (
    <div
      role="status"
      style={{
        minWidth: 320,
        maxWidth: 480,
        boxShadow: theme.effects.elevation8,
        borderRadius: theme.effects.roundedCorner2,
        opacity: toast.visible ? 1 : 0,
        transform: toast.visible ? 'translateY(0)' : 'translateY(-8px)',
        transition: 'opacity 150ms ease, transform 150ms ease',
      }}
    >
      <MessageBar
        messageBarType={barType(toast)}
        onDismiss={
          toast.type === 'loading'
            ? undefined
            : () => ReactHotToast.toast.dismiss(toast.id)
        }
        dismissButtonAriaLabel="Close"
        styles={{
          root: {
            borderRadius: theme.effects.roundedCorner2,
            padding: '8px 4px',
            ...(isInfo && { background: theme.palette.themeLight }),
          },
          icon: isInfo ? { color: theme.palette.themePrimary } : undefined,
          iconContainer: isLoading ? { display: 'none' } : undefined,
          text: {
            fontFamily: theme.fonts.medium.fontFamily,
            fontSize: theme.fonts.medium.fontSize,
          },
        }}
      >
        {isLoading ? (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Spinner size={SpinnerSize.small} />
            <span>{message as React.ReactNode}</span>
          </Stack>
        ) : (
          (message as React.ReactNode)
        )}
      </MessageBar>
    </div>
  );
};
